import React, { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { supabase } from '../../supabaseClient'

const API_URL = process.env.REACT_APP_ZOOMEY_API_URL || 'http://localhost:3000'

function QuickSharePopup({ show, onClose, itemId, sectionType, UserId, anchorRect, isDarkMode, onShareSuccess }) {
  const [connections, setConnections] = useState([])
  const [loading, setLoading] = useState(false)
  const [sharing, setSharing] = useState(null)
  const [sharedTo, setSharedTo] = useState(new Set())
  const [success, setSuccess] = useState(null)
  const [error, setError] = useState(null)
  const popupRef = useRef(null)

  useEffect(() => {
    if (!show || !UserId) return
    setSuccess(null)
    setError(null)
    setSharing(null)
    fetchData()
  }, [show, UserId, itemId])

  // Close on outside click or scroll
  useEffect(() => {
    if (!show) return
    const handleClick = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) onClose()
    }
    const handleScroll = () => onClose()
    document.addEventListener('mousedown', handleClick)
    // Delay scroll listener by 100ms to prevent mobile tap ghost-scroll from immediately closing popup
    const scrollTimer = setTimeout(() => {
      window.addEventListener('scroll', handleScroll, true)
    }, 100)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      clearTimeout(scrollTimer)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [show, onClose])

  const fetchData = async () => {
    setLoading(true)
    try {
      const { data } = await supabase.from('profiles').select('share_connection, email').eq('id', UserId).single()
      const shareConn = data?.share_connection || {}
      const senderEmail = data?.email || ''
      const accepted = Object.entries(shareConn)
        .filter(([, e]) => e.accept && !e.cancel)
        .map(([id, e]) => ({ id, email: e.email }))
      setConnections(accepted)

      // Check which users already have this item shared
      const alreadyShared = new Set()
      for (const conn of accepted) {
        try {
          const res = await fetch(`${API_URL}/api/sharing/tasks-copy-status?sender_id=${UserId}&receiver_id=${conn.id}&section_type=${sectionType}`)
          const statusData = await res.json()
          const ids = new Set((statusData?.shared_task_ids || []).map(String))
          if (ids.has(String(itemId))) alreadyShared.add(conn.id)
        } catch (_) { /* ignore */ }
      }
      setSharedTo(alreadyShared)
    } catch (e) { console.error('QuickShare: fetch error', e) }
    setLoading(false)
  }

  const handleShare = async (receiverId) => {
    setSharing(receiverId)
    setError(null)
    try {
      const { data: profile } = await supabase.from('profiles').select('email').eq('id', UserId).single()
      const senderEmail = profile?.email || ''

      const res = await fetch(`${API_URL}/api/sharing/tasks-copy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_id: UserId,
          sender_email: senderEmail,
          receiver_id: receiverId,
          task_ids: [itemId],
          section_type: sectionType,
        }),
      })
      const result = await res.json()
      if (!res.ok) throw new Error(result.error || 'Failed to share')

      setSharedTo(prev => new Set([...prev, receiverId]))
      setSuccess(receiverId)
      if (onShareSuccess) onShareSuccess()
      setTimeout(() => { setSuccess(null) }, 1500)
    } catch (e) {
      console.error('QuickShare: share error', e)
      setError('Failed to share')
    }
    setSharing(null)
  }

  if (!show) return null

  const bgColor = isDarkMode ? '#1e1e2e' : '#fff'
  const borderColor = isDarkMode ? '#444' : '#ddd'
  const textColor = isDarkMode ? '#e0e0e0' : '#333'
  const hoverBg = isDarkMode ? '#2a2a3d' : '#f5f5f5'

  return ReactDOM.createPortal(
    <div
      ref={popupRef}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: anchorRect ? anchorRect.bottom + 4 : 0,
        left: anchorRect ? Math.max(8, anchorRect.right - 240) : 0,
        zIndex: 99999,
        background: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '8px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        width: '240px',
        padding: '8px 0',
      }}
    >
      <div style={{ padding: '4px 12px 6px', fontSize: '0.7rem', fontWeight: 700, color: '#14b8a6', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Quick Share
      </div>

      {loading ? (
        <div style={{ padding: '12px', textAlign: 'center', fontSize: '0.75rem', color: '#999' }}>Loading...</div>
      ) : connections.length === 0 ? (
        <div style={{ padding: '12px', textAlign: 'center', fontSize: '0.75rem', color: '#999' }}>No connections</div>
      ) : (
        connections.map(conn => {
          const isAlready = sharedTo.has(conn.id)
          const isSharing = sharing === conn.id
          const justShared = success === conn.id

          return (
            <div
              key={conn.id}
              onClick={(e) => {
                e.stopPropagation()
                if (!isAlready && !isSharing) handleShare(conn.id)
              }}
              style={{
                padding: '6px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: isAlready ? 'default' : 'pointer',
                opacity: isAlready ? 0.5 : 1,
                fontSize: '0.78rem',
                color: textColor,
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { if (!isAlready) e.currentTarget.style.background = hoverBg }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
            >
              <i className="bi bi-person-circle" style={{ fontSize: '1rem', color: '#14b8a6' }}></i>
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {conn.email}
              </span>
              {isSharing && <i className="bi bi-arrow-repeat" style={{ fontSize: '0.8rem', color: '#14b8a6', animation: 'spin 1s linear infinite' }}></i>}
              {justShared && <i className="bi bi-check-circle-fill" style={{ fontSize: '0.8rem', color: '#22c55e' }}></i>}
              {isAlready && !justShared && <span style={{ fontSize: '0.6rem', color: '#999' }}>Shared</span>}
            </div>
          )
        })
      )}

      {error && <div style={{ padding: '4px 12px', fontSize: '0.7rem', color: '#ef4444' }}>{error}</div>}
    </div>,
    document.body
  )
}

export default QuickSharePopup
